/**
 * Droits : la matrice et la fonction qui la lit (5.4).
 *
 * `can({ role, rights, scope }, "invoices:issue")` répond oui ou non. L'API en
 * fait un 403, la régie en fait un bouton masqué.
 */
import { AppError } from '../errors.js';
import { ACTIONS, ressourceDe } from './actions.js';
import { MATRICE } from './matrice.js';
import { AUCUN_DROIT, DROITS_FINS, porteeDuRole } from './roles.js';
export * from './actions.js';
export * from './matrice.js';
export * from './roles.js';
/** La règle qui gouverne une action. */
export function regleDe(action) {
    return MATRICE[action];
}
/** Ce sujet a-t-il le droit de faire cette action ? */
export function can(subject, action) {
    const regle = regleDe(action);
    if (regle.portee !== subject.scope)
        return false;
    if (porteeDuRole(subject.role) !== subject.scope)
        return false;
    if (regle.roles.includes(subject.role))
        return true;
    if (regle.droit === undefined)
        return false;
    if (!regle.droit.roles.includes(subject.role))
        return false;
    return subject.rights[regle.droit.nom] === true;
}
/** Comme `can`, mais lève une erreur normalisée quand c'est non. */
export function assertCan(subject, action) {
    if (!can(subject, action)) {
        throw new AppError('acces.refuse', 'Votre rôle ne permet pas cette action.', {
            role: subject.role,
            action,
        });
    }
}
/** Toutes les actions permises à ce sujet : ce que la régie affiche. */
export function actionsPermises(subject) {
    return ACTIONS.filter((action) => can(subject, action));
}
/** Les ressources auxquelles ce sujet touche, au moins en lecture. */
export function ressourcesPermises(subject) {
    const vues = new Set();
    for (const action of actionsPermises(subject)) {
        vues.add(ressourceDe(action));
    }
    return [...vues];
}
/** Un sujet sans aucun droit fin, pour les tests et les valeurs par défaut. */
export function sujet(role, rights = {}) {
    return { role, rights: { ...AUCUN_DROIT, ...rights }, scope: porteeDuRole(role) };
}
/**
 * Les rôles auxquels un droit fin peut être ACCORDÉ (M7) : ceux que la matrice
 * nomme dans `droit.roles`, pour au moins une action. Dérivé, jamais recopié :
 * élargir un droit dans `matrice.ts` l'élargit ici.
 */
export function rolesPouvantRecevoir(droit) {
    const roles = new Set();
    for (const action of ACTIONS) {
        const regle = regleDe(action);
        if (regle.droit?.nom === droit)
            for (const role of regle.droit.roles)
                roles.add(role);
    }
    return [...roles];
}
/** Ce rôle peut-il recevoir ce droit fin ? */
export function droitAccordable(role, droit) {
    return rolesPouvantRecevoir(droit).includes(role);
}
/**
 * Droits fins qui ouvrent un accès sensible (décision du 21 octobre 2026) :
 * le coffre confidentiel, le prix artiste, la facturation. Qui en détient un
 * doit enrôler le second facteur, comme la direction. `publierSite` n'en est
 * pas : l'édition du site revient au support d'Artosera depuis le 20 octobre.
 */
export const DROITS_FINS_SENSIBLES = [
    'coffreConfidentiel',
    'voirPrixArtiste',
    'emettreFactures',
];
/**
 * Ce rôle détient-il un droit fin sensible qu'il peut exercer ? Un droit posé
 * sur un rôle qui ne peut pas le recevoir (la Lecture) n'ouvre rien : il ne
 * compte pas. Le module `roles-fins` n'entre pas en jeu : coupé, il suspend
 * les droits sans les retirer, et les rallumer ne doit pas ouvrir le
 * confidentiel à un compte sans second facteur.
 */
export function detientUnDroitSensible(role, droits) {
    return DROITS_FINS_SENSIBLES.some((droit) => droits[droit] && droitAccordable(role, droit));
}
/** Les droits VRAIS de `droits` que ce rôle ne peut pas recevoir, dans l'ordre de `DROITS_FINS`. */
export function droitsNonAccordables(role, droits) {
    return DROITS_FINS.filter((droit) => droits[droit] && !droitAccordable(role, droit));
}
//# sourceMappingURL=index.js.map